/**
 * Routes the Ctrl+Shift+F (Cmd+Shift+F) "Jump to column" shortcut to exactly
 * one grid. Some pages mount several grids at once (the Pricetool "All" tab
 * shows Listings and Inventory, and detail panels can nest more), so instead
 * of every grid listening on window and every picker opening, each grid
 * registers here and a single shared listener opens the picker of:
 *   1. the grid the user last focused or pressed a pointer inside, else
 *   2. the first registered top-level grid that is on screen.
 * The window/document listeners exist only while at least one grid is mounted.
 */

export interface JumpToColumnTarget {
  /** Element that counts as "inside this grid" for interaction tracking. */
  getRoot: () => HTMLElement | null;
  /** Opens this grid's picker. */
  open: () => void;
}

// In registration (mount) order
const targets: JumpToColumnTarget[] = [];
let lastInteracted: JumpToColumnTarget | null = null;

export function isJumpToColumnShortcut(
  e: Pick<KeyboardEvent, "key" | "ctrlKey" | "metaKey" | "shiftKey" | "altKey">
): boolean {
  return (
    (e.ctrlKey || e.metaKey) &&
    e.shiftKey &&
    !e.altKey &&
    e.key.toLowerCase() === "f"
  );
}

// Unmounted or display:none grids (e.g. an inactive tab) can't show a picker
const isOnScreen = (target: JumpToColumnTarget) => {
  const root = target.getRoot();
  return !!root && root.getClientRects().length > 0;
};

/**
 * The innermost registered grid whose root contains `node`, so a click inside
 * a nested sub-grid is credited to that grid rather than to its parent.
 */
function findTargetContaining(node: Node | null): JumpToColumnTarget | null {
  if (!node) return null;
  let best: JumpToColumnTarget | null = null;
  let bestRoot: HTMLElement | null = null;
  for (const target of targets) {
    const root = target.getRoot();
    if (!root || !root.contains(node)) continue;
    if (!bestRoot || bestRoot.contains(root)) {
      best = target;
      bestRoot = root;
    }
  }
  return best;
}

const isTopLevel = (target: JumpToColumnTarget) => {
  const root = target.getRoot();
  if (!root) return false;
  return !targets.some((other) => {
    const otherRoot = other.getRoot();
    return other !== target && !!otherRoot && otherRoot.contains(root);
  });
};

/** The grid the shortcut should open the picker for, if any. */
export function resolveJumpToColumnTarget(): JumpToColumnTarget | null {
  if (lastInteracted && isOnScreen(lastInteracted)) return lastInteracted;
  return targets.find((t) => isTopLevel(t) && isOnScreen(t)) ?? null;
}

const handleInteraction = (e: Event) => {
  const target = findTargetContaining(e.target as Node | null);
  if (target) lastInteracted = target;
};

const handleKeyDown = (e: KeyboardEvent) => {
  if (e.defaultPrevented || !isJumpToColumnShortcut(e)) return;
  // Leave the keyboard to an open dialog, menu or popover (including an
  // already-open picker) instead of opening a picker behind it
  if (document.activeElement?.closest('.MuiModal-root, [role="dialog"]')) {
    return;
  }

  const target = resolveJumpToColumnTarget();
  if (!target) return;
  e.preventDefault();
  target.open();
};

/**
 * Registers a grid's picker. Returns the cleanup to call on unmount, which
 * also tears the shared listeners down once the last grid is gone.
 */
export function registerJumpToColumnTarget(
  target: JumpToColumnTarget
): () => void {
  targets.push(target);
  if (targets.length === 1) {
    window.addEventListener("keydown", handleKeyDown);
    // Capture phase so grids that stop propagation still count as interacted
    document.addEventListener("pointerdown", handleInteraction, true);
    document.addEventListener("focusin", handleInteraction, true);
  }

  return () => {
    const index = targets.indexOf(target);
    if (index === -1) return;
    targets.splice(index, 1);
    if (lastInteracted === target) lastInteracted = null;
    if (targets.length === 0) {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handleInteraction, true);
      document.removeEventListener("focusin", handleInteraction, true);
    }
  };
}
